import { useRef } from 'react';

import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';

import getConfig from 'next/config';

import fastDeepEqual from 'fast-deep-equal';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { getAkamaiZipCode } from '@atc/akamai-fns';
import { useBrand } from '@atc/brand';
import { getAndSaveConsumerMetadata } from '@atc/consumer-metadata';

import authDuck from '@/ducks/authDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import invResultsDuck from '@/ducks/inv/invResultsDuck';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpNoSearchResultsDuck from '@/ducks/srp/srpNoSearchResultsDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import userDuck from '@/ducks/userDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useExperiments from '@/hooks/useExperiments';

import AlphaModule from '@/modules/AlphaModule';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

import dataLayerTransform from '@/transformers/dataLayerTransform.js';

import { pageIdsByPageName } from './analyticsUtils';

export default function useDataLayer(data = {}) {

    // Ref authConsumerMetadataRef so we don't have to hit aws everytime
    const authConsumerMetadataRef = useRef({});
    const isLoggedInRef = useRef(false);

    // TODO we should store this somewhere instead of pulling it out of context and passing it in to the hook
    const { totalBoostCount } = data;

    const [cookies = {}] = useCookies(['abc', 'pxa_id', 'pixall_abc']);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const dataIsland = useSelector(birfDuck.selectors.getPageData);
    const zip = useSelector(userDuck.selectors.getZip);
    const inventory = useSelector(inventoryDuck.selectors.selectHydratedInventory);
    const personalization = useSelector(PersonalizationEngineModule.duck.selectors.selectConsumerInsights, _isEqual);
    const pageName = useSelector(currentPageNameDuck.selectors.getDuckState);
    const consumerAdTargets = useSelector(PersonalizationEngineModule.duck.selectors.selectConsumerAdTargets, _isEqual);
    const consumerAdTargetsWithoutCAMPBias = useSelector(PersonalizationEngineModule.duck.selectors.selectConsumerAdTargetsWithoutCAMPBias, _isEqual);
    const consumerMarketingSegments = useSelector(PersonalizationEngineModule.duck.selectors.selectConsumerMarketingSegments, _isEqual);

    const userZipCode = getAkamaiZipCode();

    const isVdp = ['vdp', 'inv'].includes(pageName);

    // This is to ensure we get the latest isLoggedIn state of user when consumerMetadata is called
    isLoggedInRef.current = useSelector(authDuck.selectors.isLoggedIn);

    const consumerMetadata = async () => {

        const isUserLoggedIn = isLoggedInRef.current;
        const hasAuthData = !_isEmpty(authConsumerMetadataRef.current);

        // If user is logged in, get from authentication
        // Otherwise get them from api
        const consumerMetadataResponse = isUserLoggedIn && hasAuthData ? { ...authConsumerMetadataRef.current } : await getAndSaveConsumerMetadata({
            isLoggedIn: isUserLoggedIn,
        });

        // Reset authConsumerMetadata ref
        // For edge case when user login, log out and then relogin with another account
        authConsumerMetadataRef.current = isUserLoggedIn ? { ...authConsumerMetadataRef.current } : {};

        return { ...consumerMetadataResponse };
    };

    const { brand } = useBrand();
    const device = useDevice();
    const { publicRuntimeConfig } = getConfig();

    // TODO: BONNET NEXT - suspense - this code is suspcious and at one point seemed to be breaking suspense if not broken into separate selectors
    const { vdpActiveResults, invActiveResults, filters, sort, currentPage, showNoResults } = useSelector((state) => {
        if (isVdp) {
            return {
                vdpActiveResults: vdpResultsDuck.selectors.getActiveResults(state),
                invActiveResults: invResultsDuck.selectors.getInvActiveInventory(state),
            };
        }

        return {
            filters: srpFiltersDuck.selectors.getRequestValues(state),
            sort: srpSortDuck.selectors.getDuckState(state),
            currentPage: srpPaginationDuck.selectors.getDuckState(state)?.currentPage,
            showNoResults: srpNoSearchResultsDuck.selectors.getDuckState(state)?.showNoResults,
        };
    }, fastDeepEqual);

    let vehicle;
    let expiredVdp = false;

    // should we get active vehicle on SRP?
    // if not, is this why telMetric tag is missing data? is theis why GA360 events missing CDs 100-160?
    if (Array.isArray(vdpActiveResults) && vdpActiveResults.length === 1 && pageName === 'vdp') {
        vehicle = inventory[vdpActiveResults[0]];
    }

    if (pageName === 'inv') {
        vehicle = invActiveResults;
    }

    if (isVdp && !vehicle) {
        expiredVdp = true;
    }

    const overrideFilterValues = {};

    if (filters) {
        const { homeServices = [], productTypes = [] } = filters;

        if (homeServices.length > 0 || productTypes.length > 0) {
            overrideFilterValues.productTypes = [...productTypes, ...homeServices];
        }
    }

    const experience = query?.experience;

    const {
        siteSection = '',
        pageType = '',
        detailPageName = '',
        cdlDetailPageName = '',
    } = dataLayerTransform({ pageName, showNoResults, expiredVdp, experience });

    const {
        pg = '',
        brandPg = '',
    } = pageIdsByPageName[pageName]?.({
        brand,
        ...vehicle,
        ...filters,
        showNoResults,
        query,
    }) || {};

    const matchAlphaZone = useSelector(AlphaModule.duck.selectors.getAlphaZoneData);
    const isAlphaMarket = useSelector(AlphaModule.duck.selectors.isAlphaMarket);
    const { getActiveExperiments } = useExperiments();
    const activeExperiments = getActiveExperiments(true);
    const dmaCode = dataIsland?.page?.location?.dma;

    return {
        // NOTE: Catalog should be 'atc' instead of derived from the brand until CADS service is implemented
        catalog: 'atc',
        brand,
        consumerInsights: personalization,
        consumerAdTargets,
        consumerAdTargetsWithoutCAMPBias,
        consumerMarketingSegments,
        consumerMetadata,
        application: {
            name: 'web',
            environment: publicRuntimeConfig.environment === 'prod' ? 'production' : 'qa',
            version: '1.0.0',
        },

        get user() {
            return {
                adBlockerState: 'off',
                get isLoggedIn() {
                    return typeof window !== 'undefined' && (!!isLoggedInRef.current || '0');
                },
                ivtMoatFlag: '0',
                zipCode: userZipCode,
                dma: dataIsland?.page?.location?.dma,
            };
        },
        page: {
            pageName,
            pg,
            brandPg,
            siteSection,
            pageType,
            cdlDetailPageName,
            detailPageName,
            experimentId: activeExperiments,
            pixallId: cookies.pxa_id,
            pixallABC: cookies.abc || cookies.pixall_abc,
            pixallPageInstanceId: dataIsland?.page?.BIRF?.pg_inst,
            testName: activeExperiments,
            // SRP will have 0 as default
            // VDP did not have this value, should this be 0, undefined or anything else?
            experimentBoosts: totalBoostCount,
        },
        search: {
            pagination: currentPage,
            // undefined means empty, null is a valid value, leave keyword: null here to be safe
            keyword: null,
            sortType: sort,
            filters: {
                ...filters,
                ...overrideFilterValues,
            },
            zipCode: zip,
        },

        device,

        inventory,

        query,

        vehicle,

        matchAlphaZone: isAlphaMarket ? {
            matchedAlphaZoneCode: dmaCode,
        } : matchAlphaZone,
    };
}
